import { useMemo, useState } from 'react';

import { SortingState } from '@tanstack/react-table';

import { useServerPagination } from '@/components/ui/table/useServerPagination';
import { usePersistedDateRange } from '@/hooks/usePersistedDateRange';
import { getFilterValues, useTableFilters } from '@/hooks/useTableFilters';
import { EMPTY_DATE_SELECTION } from '@/modules/analytics/dateSelection';
import {
  RUNS_DATE_STORAGE_KEY,
  RUNS_FILTERS_STORAGE_KEY,
  SORT_FIELD_MAP,
} from '@/modules/simulations/components/Overview/RunsTable/constants';
import useRunsCadence from '@/modules/simulations/hooks/useRunsCadence';
import { useSimulationRuns } from '@/modules/simulations/hooks/useSimulationRuns';
import useSimulations from '@/modules/simulations/hooks/useSimulations';
import type { RunsListParams, RunStatus, TriggerType } from '@/modules/simulations/types';
import { getAnalyticsRangeLocal } from '@/util/analytics';

const FAILS_TO_BOOL: Record<string, boolean | undefined> = { true: true, false: false };

export const useRunsPage = (brainId?: string) => {
  const { cancelRun, deleteRun } = useSimulations(brainId);
  const { getCadence } = useRunsCadence(brainId);

  const [sorting, setSorting] = useState<SortingState>([]);

  const sortParam = useMemo(() => {
    const head = sorting[0];
    if (!head) {
      return undefined;
    }
    const field = SORT_FIELD_MAP[head.id];
    if (!field) {
      return undefined;
    }
    return head.desc ? `-${field}` : field;
  }, [sorting]);

  const { filters, activeFilterCount, setFilter, removeFilter, resetFilters } = useTableFilters({
    storageKey: RUNS_FILTERS_STORAGE_KEY,
  });

  const { dateRange, selection, setSelection } = usePersistedDateRange(
    RUNS_DATE_STORAGE_KEY,
    EMPTY_DATE_SELECTION
  );

  const runParams = useMemo<RunsListParams>(() => {
    const status = (getFilterValues(filters, 'status') as RunStatus[]).slice().sort();
    const fails = getFilterValues(filters, 'fails')[0];
    const trigger = getFilterValues(filters, 'trigger')[0] as TriggerType | undefined;
    const range = getAnalyticsRangeLocal(dateRange.startDate, dateRange.endDate);
    return {
      status: status.length ? status : undefined,
      with_fails: FAILS_TO_BOOL[fails],
      trigger_type: trigger,
      sort: sortParam,
      started_after: range.startDate ?? undefined,
      started_before: range.endDate ?? undefined,
    };
  }, [filters, sortParam, dateRange]);

  const {
    runsData,
    runs,
    listStatus,
    isLoading,
    fetchNextPage,
    hasNextPage: serverHasNextPage,
    isFetchingNextPage,
  } = useSimulationRuns(brainId, runParams);

  const { currentPageIndex, setCurrentPageIndex, goToNextPage, hasNextPage, currentPageData } =
    useServerPagination(runsData?.pages ?? [], fetchNextPage, {
      dataSelector: (page) => page?.runs ?? [],
      resetDependencies: [brainId, runParams],
      serverHasNextPage,
      externalIsFetching: isFetchingNextPage,
    });

  const rowCount = runsData?.pages[0]?.pagination.total ?? runs.length;

  return {
    runs,
    currentPageData,
    rowCount,
    listStatus,
    isLoading,
    isFetchingNextPage,
    currentPageIndex,
    setCurrentPageIndex,
    goToNextPage,
    hasNextPage,
    sorting,
    setSorting,
    filters,
    activeFilterCount,
    setFilter,
    removeFilter,
    resetFilters,
    dateRange,
    selection,
    setSelection,
    getCadence,
    cancelRun,
    deleteRun,
  };
};

export type UseRunsPageReturn = ReturnType<typeof useRunsPage>;
